
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { Header, Job, User } from '@/models/main';

import KJobTimeline from './components/KJobTimeline/KJobTimeline.vue';
import KStatusChart from './components/KStatusChart/KStatusChart.vue';

import IconWithTooltip from '@/components/UI/iconWithTooltip/IconWithTooltip.vue';
import { warehouseStatusList } from '@/utils/PortalConstants';
import { getAttributeIcon, getAttributeDescription, hasAttribute } from '@/utils/Common';
import { mapState } from 'vuex';

import { format, utcToZonedTime } from 'date-fns-tz';
import { timeZoneDefault, statusChartLabels } from '@/utils/PortalConstants';
import { formatDateStringWithTZ } from '@/utils/InputProcessingUtilities';
import { getBaseName } from '@/utils/Common';

@Component({
  components: {
    IconWithTooltip,
    KStatusChart,
    KJobTimeline,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
  data() {
    return {
      warehouseStatusList,
      statusChartLabels,
    };
  },
})
export default class JobTable extends Vue {
  @Prop({ type: Boolean, default: false }) private readonly loading: boolean | undefined;
  @Prop(Boolean) private readonly showEdit: boolean | undefined;
  @Prop(Boolean) public readonly showJobEditBtn: boolean;
  @Prop(Boolean) private readonly disableSort: boolean | undefined;
  @Prop(String) private readonly search: string | undefined;
  @Prop(Array) private readonly jobs: Job | undefined;
  @Prop(Array) private readonly headersProps: Header[];

  private user!: User;
  private today = '';
  // to use func in the template
  public getAttributeIcon = getAttributeIcon;
  public getAttributeDescription = getAttributeDescription;
  public hasAttribute = hasAttribute;
  private formatDateStringWithTZ = formatDateStringWithTZ;
  public getBaseName = getBaseName;

  private expanded: Job[] = [];
  private headers: Header[] = [
    { text: 'Id', value: 'id', align: 'left custom' },
    { text: 'Customer Code', value: 'customer_code', align: 'left custom' },
    { text: 'Reference 1', value: 'ref1', align: 'left custom' },
    { text: 'Suburb Name', value: 'stop2_name', align: 'left custom' },
    { text: 'Suburb State', value: 'suburb_state2', align: 'left custom' },
    { text: 'Stop Name', value: 'suburb_name2', align: 'left custom' },
    { text: 'Stop Number', value: 'main_stop', align: 'left custom' },
    { text: 'Driver', value: 'driver2', align: 'left custom' },
    { text: 'Delay Time', value: 'currDelay', align: 'left custom', sortable: false },
    { text: 'Warehouse', value: 'wh_status', align: 'left custom', sortable: false },
    { text: 'Actions', value: 'actions', align: 'left custom' },
    { text: 'Attributes', value: 'attributes', align: 'left custom', sortable: false },
    {
      text: 'Booked ─ Allocated ─ Stop1 ── Stop2 ── POD ─── Completed',
      value: 'statusBar',
      sortable: false,
      align: 'left custom',
      filterable: false,
    },
  ];
  private previousSortBy = '';
  private previousSortDesc = false;

  public env = process.env.NODE_ENV;

  public openInEditModalClicked(unique_id: string): void {
    this.$emit('openInEditModal', unique_id);
  }

  private wareHouseLinkClick(item): void {
    // correct direct link to the quant in Odoo (Only for "D" warehouses)
    if (item.wh_status && item.wh_status === 'D' && item.quant_id) {
      window.open(
        `${process.env.VUE_APP_URL}/web#id=${item.quant_id}&view_type=form&model=stock.quant&action=176`,
        '_blank',
      );
    }
  }

  private hideNotForStaffColumns(): void {
    this.headers = this.headers.filter((h: Header) => {
      return (
        h.text !== 'Customer Code' &&
        h.text !== 'Driver' &&
        h.text !== 'Delay Time' &&
        h.text !== 'Warehouse' &&
        h.text !== 'Action'
      );
    });
  }

  // Show current driver on the table
  // Sometimes drivers could be different on different stops (ex. Split-to-Base jobs)
  private currDriver(item: Job): string {
    const driverVal = item.main_stop === 1 ? item.driver1 : item.driver2;
    return driverVal !== 'None' ? driverVal.toString() : '';
  }

  /**
   * Handle data table options update event (including sort changes)
   * Creates a sorted copy of the jobs array based on current sort settings
   * and emits it to the parent component
   */
  private handleOptionsUpdate(options: { sortBy?: string[]; sortDesc?: boolean[] }): void {
    // Only emit when sort options have changed
    if (!options.sortBy || !options.sortBy.length) {
      return;
    }
    const sortBy = options.sortBy[0];
    const sortDesc = options.sortDesc?.[0] || false;
    // Skip if sort hasn't changed
    if (this.previousSortBy === sortBy && this.previousSortDesc === sortDesc) {
      return;
    }
    this.previousSortBy = sortBy;
    this.previousSortDesc = sortDesc;
    // Make sure this.jobs exists and is an array
    if (!this.jobs || !Array.isArray(this.jobs)) {
      return;
    }
    // Create manually sorted array
    const sortedItems = [...this.jobs].sort((a, b) => {
      // Special handling for Driver column
      if (sortBy === 'driver2') {
        const driverA = this.currDriver(a) || '';
        const driverB = this.currDriver(b) || '';
        // Special case for NOT ALLOCATED - should always be at the end
        if (driverA.toUpperCase() === 'NOT ALLOCATED' && driverB.toUpperCase() !== 'NOT ALLOCATED') {
          return sortDesc ? -1 : 1;
        }
        if (driverA.toUpperCase() !== 'NOT ALLOCATED' && driverB.toUpperCase() === 'NOT ALLOCATED') {
          return sortDesc ? 1 : -1;
        }
        // If both are numeric, compare as numbers
        const numA = parseInt(driverA, 10);
        const numB = parseInt(driverB, 10);
        if (!isNaN(numA) && !isNaN(numB)) {
          return sortDesc ? numB - numA : numA - numB;
        }
        // For mixed or non-numeric values, use string comparison
        return sortDesc ? driverB.localeCompare(driverA) : driverA.localeCompare(driverB);
      } else {
        // For other columns, use default comparison
        const valueA = a[sortBy];
        const valueB = b[sortBy];
        if (typeof valueA === 'string' && typeof valueB === 'string') {
          return sortDesc ? valueB.localeCompare(valueA) : valueA.localeCompare(valueB);
        }
        return sortDesc ? valueB - valueA : valueA - valueB;
      }
    });

    // Emit sorted items to parent
    this.$emit('sortedItemsChanged', sortedItems);
  }

  // Show current job delay in hours. Backend sends it in seconds
  private currDelay(item: Job) {
    if (item.delay_time && item.main_stop !== 1) {
      const numHours = Math.floor(parseInt(item.delay_time, 10) / 60 / 60);
      return `${numHours} ${numHours > 1 ? 'hrs' : 'hr'}`;
    } else {
      return '';
    }
  }

  private expandRow(job: Job): void {
    if (this.expanded[0] === job) {
      this.expanded = [];
    } else {
      this.expanded = [];
      this.expanded.push(job);
    }
  }
  private rowClasses(item) {
    if (item.priority) {
      return ['rowTable', 'priority'];
    }
    if (item.is_rfc) {
      return ['rowTable', 'changeRequest'];
    }
    return ['rowTable'];
  }
  private mounted(): void {
    const userTimeZone = this.user.timezone || timeZoneDefault;
    this.today = format(utcToZonedTime(new Date(), userTimeZone), 'yyyy-MM-dd');
    if (this.headersProps) {
      this.headers = this.headersProps;
    }
    if (!this.$ability.can('staff', 'Job')) {
      this.hideNotForStaffColumns();
    }
  }

  // If these conditions change, we need to change the job filtering in the same way
  // function "filteredItems" in Dashboard.vue
  private showSplitToBase(item) {
    if (
      item.split_base &&
      item.split_base !== 0 &&
      (!item.driver2 || item.driver2 === 0 || item.driver2 === 'NOT ALLOCATED')
    ) {
      return true;
    }
    return false;
  }
}
